import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

import { Camera } from '@capacitor/camera';
import * as Sentry from '@sentry/browser';

import { useDevice } from '@hooks/useDevice';

import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';

interface MediaUploadFieldProps {
  name?: string;
  placeholder: string;
  onChange: (files: File[]) => void;
}

function generateVideoThumbnail(file: File) {
  return new Promise<string>((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');

    const url = URL.createObjectURL(file);
    video.src = url;
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';

    video.addEventListener('loadeddata', () => {
      video.currentTime = 1;
    });

    video.addEventListener('seeked', () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        resolve(URL.createObjectURL(blob));
      }, 'image/jpeg');
    });

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject('Failed to load video for preview');
    };
  });
}

const MediaItem = ({ file, onRemove }: { file: File; onRemove: () => void }) => {
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    let objectUrl: string;

    if (file.type.startsWith('video/')) {
      generateVideoThumbnail(file)
        .then((url) => setPreviewUrl(url))
        .catch((err) => Sentry.captureException(err));
    } else {
      objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <div style={{ position: 'relative' }}>
      <div className="p-1" style={{ position: 'absolute', top: 0, right: 0 }}>
        <IconButton variant="default" color="neutral" onClick={onRemove} size="sm">
          <FontAwesomeIcon name="times" />
        </IconButton>
      </div>
      <img
        className="d-block"
        src={previewUrl}
        style={{ objectFit: 'cover', width: 120, aspectRatio: '4/5', borderRadius: 8 }}
      />
    </div>
  );
};

export const MediaItems = ({
  className = '',
  selectedFiles,
  onRemoveFile,
}: {
  className?: string;
  selectedFiles: File[];
  onRemoveFile: (index: number) => void;
}) => {
  return (
    <HorizontalScroll
      items={[
        <div className={`d-flex flex-wrap gap-2 ${className}`}>
          {selectedFiles.map((file, index) => (
            <MediaItem
              key={`${file.name}-${index}`}
              file={file}
              onRemove={() => onRemoveFile(index)}
            />
          ))}
        </div>,
      ]}
    />
  );
};

export const useMediaUploadField = () => {
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleClick = async () => {
    mediaInputRef.current?.click();
  };

  const handleChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const newFiles = Array.from(e.target.files);
    const updatedFiles = [...selectedFiles, ...newFiles];
    setSelectedFiles(updatedFiles);

    // Reset input so the same file can be re-added if removed
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const updatedValue = [...selectedFiles];
    updatedValue.splice(index, 1);
    setSelectedFiles(updatedValue);
  };

  return {
    mediaInputRef,
    selectedFiles,
    handleClick,
    handleChangeFile,
    handleRemoveFile,
  };
};

export const MediaUploadField: FC<MediaUploadFieldProps> = ({ name, placeholder, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleClick = async () => {
    inputRef.current?.click();
  };

  const handleChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const newFiles = Array.from(e.target.files);
    const updatedFiles = [...selectedFiles, ...newFiles];
    setSelectedFiles(updatedFiles);
    onChange(updatedFiles);

    // Reset input so the same file can be re-added if removed
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const updatedValue = [...selectedFiles];
    updatedValue.splice(index, 1);
    setSelectedFiles(updatedValue);
    onChange(updatedValue);
  };

  return (
    <div>
      {selectedFiles.length > 0 && (
        <HorizontalScroll
          items={[
            <div className="d-flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <MediaItem
                  key={`${file.name}-${index}`}
                  file={file}
                  onRemove={() => handleRemoveFile(index)}
                />
              ))}
            </div>,
          ]}
        />
      )}

      <div onClick={handleClick} style={{ minHeight: 32 }}>
        <div className="d-flex flex-column align-items-center py-5 px-2">
          <div className="fs-1">
            <FontAwesomeIcon name="images" />
          </div>
          <span>{placeholder}</span>
        </div>
        <div className="d-none">
          <input
            ref={inputRef}
            type="file"
            name={name}
            accept="image/*,video/*"
            multiple
            onChange={handleChangeFile}
          />
        </div>
      </div>
    </div>
  );
};
